import clsx from 'clsx';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EqualIcon,
  PlusIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InformationIcon from '@/assets/icons/information.svg';
import { CloseIcon } from '@/components/icons/close';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';

const gridTemplateColumns = '20px 112px 20px 72px 20px 105px';

interface TargetBudgetProps {
  budgetSpent: number;
  allowExpand?: boolean;
  hideTargetBudget?: boolean;
}

export function TargetBudget({
  budgetSpent,
  allowExpand = true,
  hideTargetBudget = false,
}: TargetBudgetProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { hookForm, hiringTarget, isFetching } = useEditCampaignContext();

  const { targetBudget, projectedBudget, overlapDuration, unitBudget } =
    hiringTarget;

  const { watch } = hookForm;
  const budgetType = watch('budget_type');

  const spendTitle = useMemo(() => {
    switch (budgetType) {
      case 'weekly':
        return intl.formatMessage({ defaultMessage: 'Spend per week' });
      case 'monthly':
        return intl.formatMessage({ defaultMessage: 'Spend per month' });
      case 'daily':
      case 'lifetime':
      default:
        return intl.formatMessage({ defaultMessage: 'Spend per day' });
    }
  }, [budgetType, intl]);

  const toggleOpen = () => {
    if (!allowExpand) return;
    setIsOpen(prev => !prev);
  };

  const formatValue = (value: number) => {
    if (!value) return '-';

    return `$${value}`;
  };

  // Hide the target budget if the campaign is completed.
  if (hideTargetBudget) return null;

  return (
    <div className="border-fx-brand-100 bg-fx-brand-50 flex flex-col gap-2 rounded-md border px-4 py-2">
      <button
        className="flex items-center justify-between"
        type="button"
        onClick={toggleOpen}
      >
        <div className="flex flex-col items-start gap-2">
          <Typography variant="subtitle1">
            <FormattedMessage defaultMessage="Hiring target budget" />
          </Typography>
          {isFetching ? (
            <Skeleton className="my-1 h-4 w-12" />
          ) : (
            <Typography variant="h6">{formatValue(targetBudget)}</Typography>
          )}
        </div>
        {isOpen ? (
          <ChevronDownIcon className="text-fx-primary h-4 w-4" />
        ) : (
          <ChevronRightIcon className="text-fx-primary h-4 w-4" />
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns,
            }}
          >
            <ReturnBackArrowIcon />

            <Metric label={spendTitle} value={formatValue(unitBudget)} />

            <CloseIcon className="text-fx-primary mt-0.5" />

            <span className="flex items-start gap-1">
              <Metric
                label={intl.formatMessage({
                  defaultMessage: 'Duration',
                })}
                value={`${overlapDuration()} days`}
              />
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'Target duration is the duration of the campaign that overlaps with the active hiring goal.',
                })}
              >
                <img src={InformationIcon} alt="" className="mt-1" />
              </Tooltip>
            </span>

            <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />

            <Metric
              label={intl.formatMessage({ defaultMessage: 'Projected spend' })}
              value={formatValue(projectedBudget)}
            />
          </div>

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns,
            }}
          >
            <PlusIcon className="text-fx-primary col-start-5 mt-0.5 h-4 w-4" />
            <Metric
              label={intl.formatMessage({ defaultMessage: 'Already spent' })}
              value={formatValue(budgetSpent)}
            />
          </div>

          <span className="bg-fx-neutral-300 flex h-px" />

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns,
            }}
          >
            <Metric
              label={intl.formatMessage({ defaultMessage: 'Target budget' })}
              value={formatValue(targetBudget)}
              className="col-start-6"
            />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  );
}
